import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/public-layout';
import { UserLayout } from '@/components/layout/user-layout';
import { AdminLayout } from '@/features/admin/components/admin-layout';
import { RouteFallback } from '@/components/layout/route-fallback';
import type { ComponentType } from 'react';

// All page modules are lazy-loaded via React Router's `lazy` route
// property. The vite build emits one chunk per page; a visitor to /
// never downloads the admin page bodies, the generation flow, or the
// account flow. New pages MUST be added through the same `lazy` shape
// or the bundler will silently bundle them into the entry.
//
// React Router 7's data router requires every `lazy` route to also
// supply a `HydrateFallback` — the element rendered during the
// initial hydration while the lazy module is still being fetched.
// Without it, the console logs "No `HydrateFallback` element provided
// to render during initial hydration" on every first paint. The
// `lazyPage` helper below adds the shared `RouteFallback` automatically.

type LazyRoute = () => Promise<{ Component: ComponentType; HydrateFallback: ComponentType }>;

const lazyPage = <T,>(
  loader: () => Promise<T>,
  selector: (module: T) => ComponentType,
): LazyRoute => async () => ({
  Component: selector(await loader()),
  HydrateFallback: RouteFallback,
});

const HomePage = lazyPage(() => import('@/pages/home-page'), (m) => m.HomePage);
const ExplorePage = lazyPage(() => import('@/pages/explore-page'), (m) => m.ExplorePage);
const CategoryPage = lazyPage(() => import('@/pages/category-page'), (m) => m.CategoryPage);
const CategoriesIndexPage = lazyPage(() => import('@/pages/categories-index-page'), (m) => m.CategoriesIndexPage);
const PromptDetailPage = lazyPage(() => import('@/pages/prompt-detail-page'), (m) => m.PromptDetailPage);
const TagPage = lazyPage(() => import('@/pages/tag-page'), (m) => m.TagPage);
const ModelPage = lazyPage(() => import('@/pages/model-page'), (m) => m.ModelPage);
const NotFoundPage = lazyPage(() => import('@/pages/not-found-page'), (m) => m.NotFoundPage);
const SearchPage = lazyPage(() => import('@/features/search/search-page'), (m) => m.SearchPage);

const AuthPage = lazyPage(() => import('@/features/auth/components/auth-page'), (m) => m.AuthPage);
const ForgotPasswordPage = lazyPage(() => import('@/features/auth/components/forgot-password-page'), (m) => m.ForgotPasswordPage);
const ResetPasswordPage = lazyPage(() => import('@/features/auth/components/reset-password-page'), (m) => m.ResetPasswordPage);

const SavedPage = lazyPage(() => import('@/features/bookmarks/saved-page'), (m) => m.SavedPage);
const CollectionsPage = lazyPage(() => import('@/features/collections/collections-page'), (m) => m.CollectionsPage);
const CollectionDetailPage = lazyPage(() => import('@/features/collections/collection-detail-page'), (m) => m.CollectionDetailPage);
const AccountPage = lazyPage(() => import('@/features/billing/account-page'), (m) => m.AccountPage);
const GenerationHistoryPage = lazyPage(() => import('@/pages/generation-history-page'), (m) => m.GenerationHistoryPage);
const GenerationDetailPage = lazyPage(() => import('@/pages/generation-detail-page'), (m) => m.GenerationDetailPage);
const GeneratePage = lazyPage(() => import('@/features/generation/generate-page'), (m) => m.GeneratePage);
const PricingPage = lazyPage(() => import('@/features/billing/pricing-page'), (m) => m.PricingPage);
const CreditsPage = lazyPage(() => import('@/pages/credits-page'), (m) => m.CreditsPage);
const FaqPage = lazyPage(() => import('@/pages/faq-page'), (m) => m.FaqPage);
const TermsPage = lazyPage(() => import('@/pages/terms-page'), (m) => m.TermsPage);
const PrivacyPage = lazyPage(() => import('@/pages/privacy-page'), (m) => m.PrivacyPage);
const RefundPolicyPage = lazyPage(() => import('@/pages/refund-policy-page'), (m) => m.RefundPolicyPage);
const ShowcasePage = lazyPage(() => import('@/pages/showcase-page'), (m) => m.ShowcasePage);
const ContactPage = lazyPage(() => import('@/pages/contact-page'), (m) => m.ContactPage);

// Guides — long-form SEO content. The index is a separate chunk; each
// individual guide is loaded by a single dynamic route that imports the
// matching data file on demand.
const GuidesIndexPage = lazyPage(
  () => import('@/pages/guides/guides-index-page'),
  (m) => m.GuidesIndexPage,
);
const DynamicGuidePage = lazyPage(
  () => import('@/pages/guides/dynamic-guide-page'),
  (m) => m.Component,
);

// Admin page bodies are the heaviest in the app. The shell (AdminLayout
// with its nav) is small and shared, so it stays in the entry. Each
// page body is its own chunk.
const AdminDashboardPage = lazyPage(() => import('@/features/admin/admin-dashboard-page'), (m) => m.AdminDashboardPage);
const AdminPromptsPage = lazyPage(() => import('@/features/admin/admin-prompts-page'), (m) => m.AdminPromptsPage);
const AdminPromptFormPage = lazyPage(() => import('@/features/admin/admin-prompt-form-page'), (m) => m.AdminPromptFormPage);
const AdminCategoriesPage = lazyPage(() => import('@/features/admin/admin-categories-page'), (m) => m.AdminCategoriesPage);
const AdminModelsPage = lazyPage(() => import('@/features/admin/admin-models-page'), (m) => m.AdminModelsPage);
const AdminUsersPage = lazyPage(() => import('@/features/admin/admin-users-page'), (m) => m.AdminUsersPage);
const AdminSubmissionsPage = lazyPage(() => import('@/features/admin/admin-submissions-page'), (m) => m.AdminSubmissionsPage);
const AdminSafetyPage = lazyPage(() => import('@/features/admin/admin-safety-page'), (m) => m.AdminSafetyPage);
const AdminShowcasePage = lazyPage(() => import('@/features/admin/admin-showcase-page'), (m) => m.AdminShowcasePage);
const AdminHeroPromptsPage = lazyPage(() => import('@/features/admin/admin-hero-prompts-page'), (m) => m.AdminHeroPromptsPage);

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', lazy: HomePage },
      { path: '/explore', lazy: ExplorePage },
      { path: '/categories', lazy: CategoriesIndexPage },
      { path: '/category', element: <Navigate to="/categories" replace /> },
      { path: '/category/:slug', lazy: CategoryPage },
      { path: '/tag/:slug', lazy: TagPage },
      { path: '/model/:slug', lazy: ModelPage },
      { path: '/prompt/:slug', lazy: PromptDetailPage },
      { path: '/search', lazy: SearchPage },
      { path: '/pricing', lazy: PricingPage },
      { path: '/credits', lazy: CreditsPage },
      { path: '/faq', lazy: FaqPage },
      { path: '/terms', lazy: TermsPage },
      { path: '/privacy', lazy: PrivacyPage },
      { path: '/refund-policy', lazy: RefundPolicyPage },
      { path: '/saved', lazy: SavedPage },
      { path: '/generate', lazy: GeneratePage },
      { path: '/signup', lazy: AuthPage },
      { path: '/signin', lazy: AuthPage },
      { path: '/forgot-password', lazy: ForgotPasswordPage },
      { path: '/reset-password', lazy: ResetPasswordPage },
      { path: '/showcase', lazy: ShowcasePage },
      { path: '/contact', lazy: ContactPage },
      { path: '/guides', lazy: GuidesIndexPage },
      { path: '/guides/:slug', lazy: DynamicGuidePage },
      { path: '*', lazy: NotFoundPage },
    ],
  },
  {
    element: <UserLayout />,
    children: [
      { path: '/account', lazy: AccountPage },
      { path: '/account/generations', lazy: GenerationHistoryPage },
      { path: '/account/generations/:id', lazy: GenerationDetailPage },
      { path: '/collections', lazy: CollectionsPage },
      { path: '/collections/:id', lazy: CollectionDetailPage },
    ],
  },
  {
    path: '/admin',
    element: (
      <AdminLayout>
        <Outlet />
      </AdminLayout>
    ),
    children: [
      { index: true, lazy: AdminDashboardPage },
      { path: 'prompts', lazy: AdminPromptsPage },
      { path: 'prompts/new', lazy: AdminPromptFormPage },
      { path: 'prompts/:id/edit', lazy: AdminPromptFormPage },
      { path: 'categories', lazy: AdminCategoriesPage },
      { path: 'models', lazy: AdminModelsPage },
      { path: 'users', lazy: AdminUsersPage },
      { path: 'submissions', lazy: AdminSubmissionsPage },
      { path: 'safety', lazy: AdminSafetyPage },
      { path: 'showcase', lazy: AdminShowcasePage },
      { path: 'hero-prompts', lazy: AdminHeroPromptsPage },
    ],
  },
]);
